import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '@apps/clos-sessions/environments';
import { MultipartUploadPlan } from '@apps/clos-sessions/services/storj-upload';

export type BinaryId = number & { __brand: 'binaryId' };

export interface Binary {
  id: BinaryId;
  name: string;
  size: number;
  type: string;
  binary: File;
  createdAt: string;
  multipartUpload?: MultipartUploadPlan;
  /** Name {@link Binary.multipartUpload} was started under; plans saved without it used `name`. */
  multipartUploadName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AssetsLocalStorage {
  protected readonly indexName = environment.indexDb.collections.assets;

  constructor(protected db: NgxIndexedDBService) {}

  save(binaries: File[]): Observable<BinaryId[]> {
    return this.db.bulkAdd(
      this.indexName,
      binaries.map((binary) => ({
        name: binary.name,
        size: binary.size,
        type: binary.type,
        binary,
        createdAt: new Date().toISOString(),
      })),
    ) as Observable<BinaryId[]>;
  }

  getMany(binaryIds: BinaryId[]): Observable<Binary[]> {
    return this.db.bulkGet(this.indexName, binaryIds);
  }

  updateMultipartUpload(
    binary: Binary,
    multipartUpload: MultipartUploadPlan | undefined,
    uploadName: string,
  ): Observable<Binary> {
    const multipartUploadName = multipartUpload ? uploadName : undefined;

    return this.db
      .update<Binary>(this.indexName, { ...binary, multipartUpload, multipartUploadName })
      .pipe(
        tap(() => {
          binary.multipartUpload = multipartUpload;
          binary.multipartUploadName = multipartUploadName;
        }),
      );
  }

  remove(binaryId: BinaryId): Observable<boolean> {
    return this.db.deleteByKey(this.indexName, binaryId);
  }
}
